import { ERROR_CODES } from './error-codes';

/**
 * fs_outcome values. Only technical_failure sets event.outcome to failure:
 * a rejected OTP or an email that is already registered is the guest's
 * outcome, not a fault in the platform.
 */
type JourneyOutcome = 'success' | 'business_rejection' | 'validation' | 'technical_failure';

type JourneyFailure = Exclude<JourneyOutcome, 'success'>;

/**
 * Error codes are the only error detail sent as a label. Anything that doesn't
 * look like a tRetail/OJ code (E0201, OJ-01-0081, OJC-01-0010) is reported as
 * 'other' so a message, email or token can never end up in a label.
 */
const ERROR_CODE_PATTERN = /^(E\d{4}|OJC?-\d{2}-\d{4})$/;

function toErrorCodeLabel(errorCode?: string | null) {
	if (!errorCode) return 'none';
	return ERROR_CODE_PATTERN.test(errorCode) ? errorCode : 'other';
}

/**
 * Codes a guest can cause. Everything else is a fault of ours, so an unmapped
 * code is a technical_failure rather than something quietly excused.
 */
const FAILURE_BY_ERROR_CODE: Record<string, JourneyFailure> = {
	[ERROR_CODES.COULD_NOT_RETRIEVE]: 'validation',
	[ERROR_CODES.RESERVATION_CANCELLED]: 'business_rejection',
	// GEM-210's checked-out code; swap for ERROR_CODES.BOOKING_CHECKED_OUT once it lands
	E0315: 'business_rejection',
};

// ErrorCategory.TECHNICAL as it arrives on the gRPC trailer
const TECHNICAL_CATEGORY = 'TECHNICAL';

/**
 * The category wins over the code. GEM-210 found tRetail answering an HTTP 500
 * with E0318, which reads as a guest outcome but is a downstream failure.
 */
function classifyFailure(errorCode?: string | null, category?: string | null): JourneyFailure {
	if (category === TECHNICAL_CATEGORY) return 'technical_failure';
	if (!errorCode) return 'technical_failure';

	return FAILURE_BY_ERROR_CODE[errorCode] ?? 'technical_failure';
}

export { toErrorCodeLabel, classifyFailure };
export type { JourneyOutcome, JourneyFailure };
